import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Field, List } from "../../../../../components/shared/Form";
import Checkbox from "../../../../../components/shared/inputs/Checkbox";
import Tooltip from "../../../../../components/shared/Tooltip";

function CheckboxCell({ name, label, disabled }: { name: string, label: string, disabled?: boolean }) {
    return (
        <div className="tw-py-1 tw-px-2 tw-flex tw-justify-center" role="cell">
            <Tooltip
                trigger={(
                    <span className="tw-inline-flex">
                        <Field name={name} valuePropName="checked" shouldUnregister={false} className="tw-mb-0">
                            <Checkbox disabled={disabled} />
                        </Field>
                    </span>
                )}
            >
                {label}
            </Tooltip>
        </div>
    );
}

function Table({ columns, fieldName = "columns", isFilter = false, canSort = true }: any) {
    const { t } = useTranslation();

    const map = useMemo(() => {
        return columns.reduce((acc: any, col: any) => {
            const accessor = col.accessorKey || col.name || col.id;
            if (accessor) acc[accessor] = { ...col, label: col?.label || col?.header };
            return acc;
        }, {});
    }, [columns]);

    const showHideCompletely = !isFilter;
    const showHideSort = !isFilter && canSort;

    const gridTemplateColumns = [
        "minmax(0, 1fr)",
        "4rem",
        showHideCompletely && "7rem",
        showHideSort && "5.5rem",
    ].filter(Boolean).join(" ");

    return (
        <div className="tw-overflow-y-auto tw-z-40 tw-h-full" role="table">
            <div
                className="tw-grid tw-sticky tw-top-0 tw-bg-white tw-z-10 tw-text-left tw-text-xs tw-text-gray-400"
                style={{ gridTemplateColumns }}
                role="row"
            >
                <div className="tw-pb-1 tw-font-normal" role="columnheader" />
                <div className="tw-pb-1 tw-font-normal tw-text-center tw-px-2 tw-whitespace-nowrap" role="columnheader">{t("hide")}</div>
                {showHideCompletely && (
                    <div className="tw-pb-1 tw-font-normal tw-text-center tw-px-2 tw-whitespace-nowrap" role="columnheader">{t("hide-completely")}</div>
                )}
                {showHideSort && (
                    <div className="tw-pb-1 tw-font-normal tw-text-center tw-px-2 tw-whitespace-nowrap" role="columnheader">{t("hide-sort")}</div>
                )}
            </div>

            <List name={fieldName}>
                {(fields) => fields.map((item: any, index) => {
                    const name = `${fieldName}.${index}`;
                    const config = map[item.accessor];

                    return (
                        <div className="tw-grid tw-items-center" style={{ gridTemplateColumns }} role="row" key={item.id}>
                            <div className="tw-py-1 tw-text-gray-700" role="cell">{config?.label}</div>
                            <CheckboxCell
                                name={`${name}.hide`}
                                label={config?.label}
                                disabled={config?.alwaysVisible}
                            />
                            {showHideCompletely && (
                                <CheckboxCell
                                    name={`${name}.hideCompletely`}
                                    label={config?.label}
                                    disabled={config?.alwaysVisible}
                                />
                            )}
                            {showHideSort && (
                                <CheckboxCell
                                    name={`${name}.hideSort`}
                                    label={config?.label}
                                    disabled={!config?.enableSorting && typeof config?.enableSorting === "boolean"}
                                />
                            )}
                        </div>
                    );
                })}
            </List>
        </div>
    );
}

export default Table;
